import { useEffect, useRef, useState } from 'react';
import type { MediaEntry } from '../../../media/types';
import './MediaIcon.css';

/** Resolved media URL base — served via worker API which proxies R2 */
const MEDIA_BASE = '/api/media';

export interface MediaIconProps {
  /**
   * Media ID for this icon. The server generates the icon from
   * the prompt stored in the database (app_catalog.media_icon_prompt).
   */
  mediaId?: string;

  /**
   * Inline media entry — use when the entry is NOT in the database
   * or you want to override DB values (e.g. non-app icons).
   */
  media?: Partial<MediaEntry>;

  /** Fallback emoji shown while loading or on error. */
  fallback?: string;

  /** Size in px (width & height). @default 64 */
  size?: number;

  /** Border radius in px. @default 14 */
  radius?: number;

  /** Additional CSS class */
  className?: string;

  /** Alt text for the generated image */
  alt?: string;
}

type LoadState = 'idle' | 'loading' | 'loaded' | 'generating' | 'error';

/**
 * MediaIcon — a smart image component with lazy-load + AI generation fallback.
 *
 * Loading strategy (cost-optimized):
 *   1. Immediately render the fallback (emoji / alt icon)
 *   2. Attempt to load from R2 public via `/api/media/<id>.png`
 *   3. On 404 → POST `/api/media/generate` to create via SDXL Lightning & store in R2
 *   4. On generation success → load the newly uploaded image
 *   5. On any failure → keep showing the fallback
 *
 * @example
 * ```tsx
 * // By registry reference (recommended)
 * <MediaIcon mediaId="chat" size={64} />
 *
 * // Inline definition
 * <MediaIcon media={{ id: 'custom', prompt: '...', fallback: '🎯', resolution: 256 }} />
 * ```
 */
export function MediaIcon({
  mediaId,
  media,
  fallback: fallbackProp,
  size = 64,
  radius = 14,
  className = '',
  alt,
}: MediaIconProps) {
  const [state, setState] = useState<LoadState>('idle');
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const attemptedGenerate = useRef(false);

  // Resolve: inline media entry or just the mediaId
  const resolvedId = media?.id ?? mediaId;
  const fallback = fallbackProp ?? media?.fallback ?? '📱';
  // Inline prompt (for non-DB icons); DB icons don't need this
  const inlinePrompt = media?.prompt;
  const resolution = media?.resolution ?? 256;
  const negativePrompt = media?.negativePrompt;
  const model = media?.model;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Main loading effect
  useEffect(() => {
    if (!resolvedId) return;

    attemptedGenerate.current = false;
    setState('loading');

    const r2Url = `${MEDIA_BASE}/${resolvedId}.png`;
    setImgSrc(r2Url);
  }, [resolvedId]);

  const handleLoad = () => {
    if (!mountedRef.current) return;
    setState('loaded');
  };

  const handleError = () => {
    if (!mountedRef.current) return;

    // Already tried generating — give up
    if (attemptedGenerate.current) {
      setState('error');
      setImgSrc(null);
      return;
    }

    // Trigger generation — server looks up prompt from DB if not provided
    if (!resolvedId) {
      setState('error');
      setImgSrc(null);
      return;
    }

    attemptedGenerate.current = true;
    setState('generating');

    // Build payload: only include prompt/resolution if provided inline.
    // For DB-driven icons, just send { id } — server resolves the rest.
    const payload: Record<string, unknown> = { id: resolvedId };
    if (inlinePrompt) {
      payload.prompt = inlinePrompt;
      payload.resolution = resolution;
      if (negativePrompt) payload.negativePrompt = negativePrompt;
      if (model) payload.model = model;
    }

    fetch(`${MEDIA_BASE}/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!mountedRef.current) return;
        if (!res.ok) throw new Error(`Generate failed: ${res.status}`);
        // Re-try loading from R2 now that the image should exist
        setState('loading');
        setImgSrc(`${MEDIA_BASE}/${resolvedId}.png?t=${Date.now()}`);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setState('error');
        setImgSrc(null);
      });
  };

  const showFallback = state !== 'loaded';
  const showImage = imgSrc && state !== 'error' && state !== 'idle';

  const classNames = [
    'media-icon',
    state === 'generating' ? 'media-icon--generating' : '',
    state === 'loaded' ? 'media-icon--loaded' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classNames}
      style={{ width: size, height: size, borderRadius: radius }}
    >
      {/* Fallback layer — visible while loading or on error */}
      {showFallback && (
        <span className="media-icon__fallback" aria-hidden={!showFallback}>
          {fallback}
        </span>
      )}

      {/* Image layer — hidden until loaded */}
      {showImage && (
        <img
          className={`media-icon__img ${state === 'loaded' ? 'media-icon__img--visible' : ''}`}
          src={imgSrc}
          alt={alt ?? media?.label ?? ''}
          width={size}
          height={size}
          loading="lazy"
          draggable={false}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}

      {/* Generating shimmer overlay */}
      {state === 'generating' && (
        <div className="media-icon__shimmer" aria-label="Generating icon..." />
      )}
    </div>
  );
}

export default MediaIcon;
